import { refreshChatBotUserAccess } from '@/api/chatBotApi.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { storecastAdminApi } from '@/api/storageForecasting/AdminApi.ts';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { IAdminAccess, IIndividualAccessInfo, IInsightsAdminAccess } from '@/interfaces/admin/users/IUserAdmin.ts';

export function ensureReducedAccessDatesAreDateObjects (access: IIndividualAccessInfo): IIndividualAccessInfo;
export function ensureReducedAccessDatesAreDateObjects (access: IIndividualAccessInfo | null | undefined): IIndividualAccessInfo | null | undefined;
export function ensureReducedAccessDatesAreDateObjects (access: IIndividualAccessInfo | null | undefined): IIndividualAccessInfo | null | undefined {
    if (!access) return access;
    if (access.startDate) access.startDate = new Date(access.startDate);
    if (access.endDate) access.endDate = new Date(access.endDate);
    return access;
}

export function ensureDatesAreDateObjects (access: IAdminAccess): IAdminAccess {
    if (access.startDate) access.startDate = new Date(access.startDate);
    if (access.endDate) access.endDate = new Date(access.endDate);

    if (access.enact.assetDatabase) {
        if (access.enact.assetDatabase.startDate) {
            access.enact.assetDatabase.startDate = new Date(access.enact.assetDatabase.startDate);
        }
        if (access.enact.assetDatabase.endDate) {
            access.enact.assetDatabase.endDate = new Date(access.enact.assetDatabase.endDate);
        }
    }

    if (access.enact.longTermPower) {
        if (access.enact.longTermPower.startDate) {
            access.enact.longTermPower.startDate = new Date(access.enact.longTermPower.startDate);
        }
        if (access.enact.longTermPower.endDate) {
            access.enact.longTermPower.endDate = new Date(access.enact.longTermPower.endDate);
        }
        access.enact.longTermPower.countryAccess.forEach(countryAccess => {
            if (countryAccess.startDate) countryAccess.startDate = new Date(countryAccess.startDate);
            if (countryAccess.endDate) countryAccess.endDate = new Date(countryAccess.endDate);
            countryAccess.options.forEach(addOn => {
                if (addOn.startDate) addOn.startDate = new Date(addOn.startDate);
                if (addOn.endDate) addOn.endDate = new Date(addOn.endDate);
            });
        });
    }

    if (access.enact.shortTermPower) {
        if (access.enact.shortTermPower.startDate) {
            access.enact.shortTermPower.startDate = new Date(access.enact.shortTermPower.startDate);
        }
        if (access.enact.shortTermPower.endDate) {
            access.enact.shortTermPower.endDate = new Date(access.enact.shortTermPower.endDate);
        }
        access.enact.shortTermPower.countryAccess.forEach(countryAccess => {
            if (countryAccess.startDate) countryAccess.startDate = new Date(countryAccess.startDate);
            if (countryAccess.endDate) countryAccess.endDate = new Date(countryAccess.endDate);
            countryAccess.addOns.forEach(addOn => {
                if (addOn.startDate) addOn.startDate = new Date(addOn.startDate);
                if (addOn.endDate) addOn.endDate = new Date(addOn.endDate);
            });
        });
        if (access.enact.shortTermPower.europeanAccess) {
            if (access.enact.shortTermPower.europeanAccess.startDate) {
                access.enact.shortTermPower.europeanAccess.startDate = new Date(access.enact.shortTermPower.europeanAccess.startDate);
            }
            if (access.enact.shortTermPower.europeanAccess.endDate) {
                access.enact.shortTermPower.europeanAccess.endDate = new Date(access.enact.shortTermPower.europeanAccess.endDate);
            }
        }
    }

    return access;
}

export function displayYesNoOrNull (value: boolean | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return value ? 'Yes' : 'No';
}

export function hasAnyFormOfInsightsAccess (insightsAccess: IInsightsAdminAccess | null): boolean {
    return !!insightsAccess && (
        insightsAccess.individualReports.length > 0
        || insightsAccess.isAdmin
        || insightsAccess.subscriptions.length > 0
    );
}

export function shouldDefaultToEnactWelcomeEmail (access: Array<IAdminAccess>): boolean {
    const hasShortTermPowerAccess = access.some(item => item.enact.shortTermPower !== null);
    const hasOtherProductAccess = access.some(item => item.enact.assetDatabase !== null
        || item.enact.longTermPower !== null
        || item.enact.residential !== null
        || hasAnyFormOfInsightsAccess(item.insights));

    return hasShortTermPowerAccess && !hasOtherProductAccess;
}

export function isValidEmail (email: string): boolean {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})*$/.test(email.trim());
}

export async function refreshUserCaches (): Promise<void> {
    const refreshPromises = [];
    refreshPromises.push(DataApi.refreshCachedUser());
    refreshPromises.push(deltaGptApi.refreshUserAccess());
    refreshPromises.push(storecastAdminApi.refreshCachedUser());
    refreshPromises.push(refreshChatBotUserAccess());
    await Promise.allSettled(refreshPromises);
}
